import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["as", "onChange", "onBlur", "defaultValue", "min", "max", "decimalDigits", "maxLength", "value"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import * as React from 'react';
import { IMaskInput } from '../../../../internal/components/IMaskInput';
import { THOUSAND_DELIMITER, FRACTIONAL_DELIMITER } from '../../../../internal/constants/number';
import { formatValueForDisplay, isSignedRange, parseInputToNumber, validateNumberInRange } from '../../utils';
import { jsx as _jsx } from "react/jsx-runtime";
export const NativeNumberInput = /*#__PURE__*/React.forwardRef(function NativeNumberInput(props, ref) {
  const {
      as,
      onChange,
      onBlur,
      defaultValue,
      min,
      max,
      decimalDigits = 0,
      maxLength,
      value
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  const [displayValue, setDisplayValue] = React.useState(() => formatValueForDisplay(value !== null && value !== void 0 ? value : defaultValue));
  const [isUserEditing, setIsUserEditing] = React.useState(false);
  const [inputMaxLength, setInputMaxLength] = React.useState(maxLength);
  // Определяем, что инпут является контролируемым (value был передан хотя бы раз)
  const isControlledRef = React.useRef(value !== undefined);
  const maskOptions = React.useMemo(() => ({
    mask: Number,
    thousandsSeparator: THOUSAND_DELIMITER,
    radix: FRACTIONAL_DELIMITER,
    signed: isSignedRange(min),
    scale: decimalDigits
  }), [min, decimalDigits]);
  const handleUserInput = React.useCallback((rawInput, mask) => {
    setIsUserEditing(true);
    setDisplayValue(rawInput);
    if (maxLength) {
      /**
       * maxLength не учитывает разделители, добавляемые маской.
       * Из-за этого вводимое число может укладываться в maxLength, а value с разделителями - нет, поэтому ввод не проходит.
       *
       * Например:
       *
       * maxLength = 5, хотим ввести число 10000, маска добавляет разделитель, получаем 10 000.
       * И при вводе такого числа получится ввести только 1000.
       *
       * Добавляя разделители к maxLength, мы покрываем этот кейс
       */
      const separatorsCount = getSeparatorsCount(mask.value, maskOptions.thousandsSeparator);
      setInputMaxLength(maxLength + separatorsCount);
    }
    const numericValue = parseInputToNumber(rawInput);
    const validatedValue = validateNumberInRange(numericValue, min, max);
    if (onChange && validatedValue !== value) {
      if (rawInput === '' || numericValue === validatedValue) {
        onChange(validatedValue);
      }
    }
  }, [maxLength, min, max, onChange, value, maskOptions.thousandsSeparator]);
  const handleInputFocus = React.useCallback(event => {
    var _rest$onFocus;
    setIsUserEditing(true);
    (_rest$onFocus = rest.onFocus) === null || _rest$onFocus === void 0 || _rest$onFocus.call(rest, event);
  }, [rest]);
  const handleInputBlur = React.useCallback(event => {
    setIsUserEditing(false);
    const numericValue = parseInputToNumber(displayValue);
    const finalValue = validateNumberInRange(numericValue, min, max);
    const formattedDisplay = formatValueForDisplay(finalValue);
    setDisplayValue(formattedDisplay);
    if (onChange && finalValue !== value) {
      onChange(finalValue);
    }
    if (onBlur) {
      onBlur(event);
    }
  }, [displayValue, min, max, onChange, value, onBlur]);
  React.useEffect(() => {
    if (value !== undefined) {
      isControlledRef.current = true;
    }
    if (!isUserEditing) {
      const currentDisplayNumeric = parseInputToNumber(displayValue);
      if (isControlledRef.current && value === undefined) {
        setDisplayValue('');
        return;
      }
      if (value && value !== currentDisplayNumeric) {
        setDisplayValue(formatValueForDisplay(value));
      }
    }
  }, [value, isUserEditing, displayValue]);
  return /*#__PURE__*/_jsx(IMaskInput, _objectSpread(_objectSpread({}, rest), {}, {
    ref: ref,
    as: as,
    type: "tel",
    value: displayValue,
    maxLength: inputMaxLength,
    options: maskOptions,
    onAccept: handleUserInput,
    onComplete: handleUserInput,
    onFocus: handleInputFocus,
    onBlur: handleInputBlur
  }));
});
function getSeparatorsCount(value, separator) {
  var _value$match;
  return ((_value$match = value.match(new RegExp(separator, 'g'))) === null || _value$match === void 0 ? void 0 : _value$match.length) || 0;
}